import { useMemo, useEffect, useCallback } from "react";
import { AnySelectOption } from "./SelectOption";

export type UseSelectOptionsParams<TOption extends AnySelectOption> = {
  allOptions: TOption[];
  visibleOptions: TOption[];
  selectedValues: TOption["value"][];
  onChange: (values: TOption["value"][]) => void;
  enableMultiSelect: boolean;
  enableDeselect: boolean;
  moveSelectedToTop: boolean;
};

export function useSelectOptions<TOption extends AnySelectOption>({
  allOptions,
  visibleOptions,
  selectedValues,
  onChange,
  enableMultiSelect,
  enableDeselect,
  moveSelectedToTop,
}: UseSelectOptionsParams<TOption>) {
  useFilterInvalidOptions(allOptions, selectedValues, onChange);

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const visibleValueSet = useMemo(
    () => new Set(visibleOptions.map((option) => option.value)),
    [visibleOptions],
  );

  const sortedOptions = useMemo(() => {
    if (!moveSelectedToTop) {
      return visibleOptions;
    }

    const selected: typeof visibleOptions = [];
    const unselected: typeof visibleOptions = [];

    for (const option of visibleOptions) {
      if (selectedValueSet.has(option.value)) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    }

    return [...selected, ...unselected];
  }, [visibleOptions, selectedValueSet, moveSelectedToTop]);

  const sortedOptionValues = useMemo(
    () => sortedOptions.map((option) => option.value),
    [sortedOptions],
  );

  const selectOption = useCallback(
    (value: TOption["value"]) => {
      if (selectedValueSet.has(value)) {
        if (!enableDeselect) {
          return;
        }

        if (enableMultiSelect) {
          onChange(selectedValues.filter((v) => v !== value));
        } else {
          onChange([]);
        }

        return;
      }

      if (enableMultiSelect) {
        onChange([...selectedValues, value]);
      } else {
        onChange([value]);
      }
    },
    [
      onChange,
      selectedValues,
      selectedValueSet,
      enableDeselect,
      enableMultiSelect,
    ],
  );

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const selectVisible = useCallback(() => {
    const next = allOptions
      .filter(
        (option) =>
          selectedValueSet.has(option.value) ||
          visibleValueSet.has(option.value),
      )
      .map((option) => option.value);

    onChange(next);
  }, [onChange, allOptions, selectedValueSet, visibleValueSet]);

  return {
    visibleOptions,
    sortedOptions,
    sortedOptionValues,

    selectedValueSet,
    visibleValueSet,

    selectOption,
    clearAll,
    selectVisible,

    hasUnselectedVisibleOptions: visibleOptions.some(
      (option) => !selectedValueSet.has(option.value),
    ),
  };
}

/**
 * if some options aren't valid any more (e.g. external update to the list
 * of options), remove them from the selected values
 */
function useFilterInvalidOptions<TOption extends AnySelectOption>(
  options: TOption[] | undefined,
  values: TOption["value"][],
  onChange: (values: TOption["value"][]) => void,
) {
  // there can be a lot of options (e.g. the Asset selector), so we cache the
  // set of options and only rebuild it when there is an actual change.
  const availableValues = useMemo(() => {
    return new Set((options ?? []).map((option) => option.value));
  }, [options]);

  useEffect(() => {
    if (!availableValues.size || values.length === 0) {
      return;
    }

    // if any of the values are invalid, filter them out
    if (values.some((value) => !availableValues.has(value))) {
      onChange(values.filter((value) => availableValues.has(value)));
    }
  }, [values, onChange, availableValues]);
}
